import { useEffect, useRef, useState } from "react"

import type {
  ProcessingPhase,
  ProcessingProgressState,
  UseProcessingProgressOptions,
} from "./types"

/**
 * Headless engine for a phased "processing / scanning" animation.
 *
 * Each phase fills from 0→100% by `stepPercent` every `tickMs`; on reaching
 * 100% the engine pauses `holdMs` and advances to the next phase. Once all
 * phases are complete it waits `finishDelayMs` and calls `onFinish`. This
 * mirrors the reference implementation (origene/mytraits) so the feel is
 * identical, but every number is configurable and the UI is left to the caller.
 *
 * Returns `{ progress, currentStep, done }` — render however you like, or use
 * {@link ProcessingScreen} for the default presentation.
 */
export function useProcessingProgress(
  phases: ProcessingPhase[],
  options: UseProcessingProgressOptions = {},
): ProcessingProgressState {
  const { tickMs = 50, stepPercent = 2, holdMs = 300, finishDelayMs = 500, onFinish } = options
  const count = phases.length

  const [progress, setProgress] = useState<number[]>(() => Array(count).fill(0))
  const [currentStep, setCurrentStep] = useState(0)

  // Reset when the phase count changes (e.g. a different config is passed in).
  // Adjusted during render — React's recommended pattern for "reset state on a
  // prop change" — rather than in an effect, which would cascade renders.
  const [prevCount, setPrevCount] = useState(count)
  if (count !== prevCount) {
    setPrevCount(count)
    setProgress(Array(count).fill(0))
    setCurrentStep(0)
  }

  // Hold onFinish in a ref so swapping the callback identity doesn't restart timers.
  const onFinishRef = useRef(onFinish)
  useEffect(() => {
    onFinishRef.current = onFinish
  }, [onFinish])

  // Drive the active phase: tick it up, then advance after a short hold.
  useEffect(() => {
    if (count === 0 || currentStep >= count) return

    if ((progress[currentStep] ?? 0) >= 100) {
      const timeout = setTimeout(() => setCurrentStep((s) => s + 1), holdMs)
      return () => clearTimeout(timeout)
    }

    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = [...prev]
        next[currentStep] = Math.min((next[currentStep] ?? 0) + stepPercent, 100)
        return next
      })
    }, tickMs)
    return () => clearInterval(interval)
  }, [progress, currentStep, count, tickMs, stepPercent, holdMs])

  const done = count > 0 && progress.every((p) => p >= 100)

  // Fire onFinish once everything is complete.
  useEffect(() => {
    if (!done) return
    const timeout = setTimeout(() => onFinishRef.current?.(), finishDelayMs)
    return () => clearTimeout(timeout)
  }, [done, finishDelayMs])

  return { progress, currentStep, done }
}
